import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import ImageField from "./ImageField";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

// Field types: text | textarea | lines | pairs | quads | image | video | imagelist | toggle
const SCHEMAS = {
  hero: [
    { k: "kicker", t: "text", l: "Kicker (small top line)" },
    { k: "heading", t: "lines", l: "Heading — one line per row, *word* = italic rose" },
    { k: "subtext", t: "textarea", l: "Subtext" },
    { k: "primary_label", t: "text", l: "Primary button label" },
    { k: "secondary_label", t: "text", l: "Secondary button label (WhatsApp)" },
    { k: "image", t: "image", l: "Hero image" },
    { k: "video", t: "video", l: "Hero video (optional — replaces image)" },
    { k: "badge_title", t: "text", l: "Floating badge title" },
    { k: "badge_sub", t: "text", l: "Floating badge subtitle" },
  ],
  marquee: [{ k: "items", t: "lines", l: "Phrases (one per line)" }],
  categories: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading (*word* = italic)" }, { k: "blurb", t: "textarea", l: "Side text" },
  ],
  products: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
  ],
  manifesto: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" },
    { k: "text", t: "textarea", l: "Intro text" }, { k: "image", t: "image", l: "Image" },
    { k: "benefits", t: "pairs", l: "Benefits — one per line: Title | Description" },
  ],
  occasions: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
    { k: "items", t: "lines", l: "Occasions (one per line)" },
    { k: "cta_kicker", t: "text", l: "CTA kicker" }, { k: "cta_heading", t: "text", l: "CTA heading" },
    { k: "cta_text", t: "textarea", l: "CTA text" }, { k: "cta_button", t: "text", l: "CTA button label" },
  ],
  testimonials: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
    { k: "items", t: "quads", l: "Testimonials — one per line: Name | City | Bought | Review | Photo (optional — paste path/URL from Media)" },
  ],
  instagram: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Text" },
    { k: "images", t: "imagelist", l: "Gallery images (one path/URL per line — paste from Media)" },
  ],
  whatsapp_cta: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" },
    { k: "heading", t: "text", l: "Card heading" }, { k: "text", t: "textarea", l: "Text" },
    { k: "button_label", t: "text", l: "Button label" }, { k: "payment_note", t: "text", l: "Small note (e.g. No COD)" },
  ],
  text: [{ k: "heading", t: "text", l: "Heading" }, { k: "body", t: "textarea", l: "Text (blank line = new paragraph)" }],
  image_text: [
    { k: "heading", t: "text", l: "Heading" }, { k: "body", t: "textarea", l: "Text" },
    { k: "image", t: "image", l: "Image" }, { k: "flip", t: "toggle", l: "Image on the right" },
    { k: "button_label", t: "text", l: "Button label (optional)" }, { k: "button_url", t: "text", l: "Button link (#contact, /p/... or https://...)" },
  ],
  video: [
    { k: "heading", t: "text", l: "Heading" }, { k: "description", t: "textarea", l: "Description" },
    { k: "video", t: "video", l: "Video (MP4/WebM upload or URL)" }, { k: "poster", t: "image", l: "Poster / thumbnail image" },
    { k: "autoplay", t: "toggle", l: "Autoplay" }, { k: "muted", t: "toggle", l: "Muted" },
    { k: "loop", t: "toggle", l: "Loop" }, { k: "controls", t: "toggle", l: "Show controls" },
  ],
  banner: [
    { k: "heading", t: "text", l: "Banner text" }, { k: "button_label", t: "text", l: "Button label" }, { k: "button_url", t: "text", l: "Button link" },
  ],
  cta: [
    { k: "heading", t: "text", l: "Heading" }, { k: "text", t: "textarea", l: "Text" },
    { k: "button_label", t: "text", l: "Button label" }, { k: "button_url", t: "text", l: "Button link" },
  ],
  gallery: [
    { k: "heading", t: "text", l: "Heading" }, { k: "images", t: "imagelist", l: "Images (one path/URL per line)" },
  ],
};

export default function SectionEditor({ section, onClose, onSaved }) {
  const [name, setName] = useState(section.name);
  const [content, setContent] = useState({ ...(section.content || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fields = SCHEMAS[section.type] || [];

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/sections/${section.id}`, { name, content });
      onSaved();
    } catch {
      setError("Could not save the section.");
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const v = content[f.k];
    if (f.t === "textarea")
      return <textarea data-testid={`sf-${f.k}`} rows={3} value={v || ""} onChange={(e) => set(f.k, e.target.value)} className={`${inputCls} resize-none`} />;
    if (f.t === "lines" || f.t === "imagelist")
      return <textarea data-testid={`sf-${f.k}`} rows={4} value={Array.isArray(v) ? v.join("\n") : v || ""} onChange={(e) => set(f.k, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={`${inputCls} resize-none`} />;
    if (f.t === "pairs")
      return (
        <textarea data-testid={`sf-${f.k}`} rows={5} className={`${inputCls} resize-none`}
          value={(v || []).map((b) => `${b.title} | ${b.description}`).join("\n")}
          onChange={(e) => set(f.k, e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [title, ...rest] = l.split("|"); return { title: title.trim(), description: rest.join("|").trim() }; }))} />
      );
    if (f.t === "quads")
      return (
        <textarea data-testid={`sf-${f.k}`} rows={5} className={`${inputCls} resize-none`}
          value={(v || []).map((t) => `${t.name} | ${t.city} | ${t.context} | ${t.quote}${t.image ? ` | ${t.image}` : ""}`).join("\n")}
          onChange={(e) => set(f.k, e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
            const parts = l.split("|");
            const [nm, city, ctx] = parts;
            const hasPhoto = parts.length >= 5;
            const quote = (hasPhoto ? parts.slice(3, -1) : parts.slice(3)).join("|").trim();
            const image = hasPhoto ? parts[parts.length - 1].trim() : "";
            return { name: nm.trim(), city: (city || "").trim(), context: (ctx || "").trim(), quote, rating: 5, ...(image ? { image } : {}) };
          }))} />
      );
    if (f.t === "image") return <ImageField label="" value={v || ""} onChange={(val) => set(f.k, val)} testid={`sf-${f.k}`} />;
    if (f.t === "video") return <ImageField label="" value={v || ""} onChange={(val) => set(f.k, val)} kind="video" testid={`sf-${f.k}`} />;
    if (f.t === "toggle")
      return (
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
          <input data-testid={`sf-${f.k}`} type="checkbox" checked={!!v} onChange={(e) => set(f.k, e.target.checked)} className="h-4 w-4 accent-[#C48B81]" />
          {f.l}
        </label>
      );
    return <input data-testid={`sf-${f.k}`} type="text" value={v || ""} onChange={(e) => set(f.k, e.target.value)} className={inputCls} />;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div data-testid="section-editor" role="dialog" aria-modal="true" className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-3xl bg-white md:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line p-6 md:px-8">
          <input
            data-testid="section-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Section name"
            className="w-full max-w-sm rounded-xl border border-transparent px-2 py-1 font-serif text-2xl font-medium outline-none transition-colors focus:border-line"
          />
          <button data-testid="section-editor-close" onClick={onClose} aria-label="Close editor" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream">
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-5 overflow-y-auto p-6 md:p-8">
          {fields.map((f) => (
            <div key={f.k}>
              {f.t !== "toggle" && f.t !== "image" && f.t !== "video" && <label className={labelCls}>{f.l}</label>}
              {(f.t === "image" || f.t === "video") && <span className={labelCls}>{f.l}</span>}
              {renderField(f)}
            </div>
          ))}
          {error && <p data-testid="section-save-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
        </div>
        <div className="flex gap-3 border-t border-line p-6 md:px-8">
          <button
            data-testid="section-save-button"
            onClick={save}
            disabled={saving}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60"
          >
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save Section"}
          </button>
          <button onClick={onClose} className="rounded-full border border-line px-6 py-4 text-sm font-medium transition-colors hover:border-ink">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
